import { ContactFilterValue, ContactsAdvancedFilter } from "@/repository/models";
import { AdvancedFilterFieldKey, advancedFilterFields } from "./types";

/** Matches the server-side cap, so a chip is never shown for a value the query drops. */
export const MaxValuesPerField = 5;

/** How many suggestions the list offers at once. */
export const MaxSuggestions = 5;

export const emptyAdvancedFilter = (): ContactsAdvancedFilter => ({
	jobTitles: [],
	departments: [],
	cities: [],
	companyNames: [],
	offices: [],
	countries: [],
});

const isFilterValue = (candidate: unknown): candidate is ContactFilterValue => {
	if (!candidate || typeof candidate !== "object") {
		return false;
	}

	const { value, label } = candidate as Partial<ContactFilterValue>;

	return typeof value === "string" && value.length > 0 && typeof label === "string";
};

/**
 * Builds a complete filter out of whatever was cached. A cache written before a field existed,
 * or by a different app version, is missing keys that the rest of the code reads as arrays.
 */
export const normalizeAdvancedFilter = (
	cached: unknown
): ContactsAdvancedFilter => {
	const filter = emptyAdvancedFilter();

	if (!cached || typeof cached !== "object") {
		return filter;
	}

	const source = cached as Record<string, unknown>;

	for (const { key } of advancedFilterFields) {
		const values = source[key];

		if (Array.isArray(values)) {
			filter[key] = values.filter(isFilterValue).slice(0, MaxValuesPerField);
		}
	}

	return filter;
};

export const flattenAdvancedFilter = (
	filter: ContactsAdvancedFilter
): { key: AdvancedFilterFieldKey; value: ContactFilterValue }[] =>
	advancedFilterFields.flatMap(({ key }) =>
		filter[key].map((value: ContactFilterValue) => ({ key, value }))
	);

export const countAdvancedFilterValues = (
	filter: ContactsAdvancedFilter
): number => flattenAdvancedFilter(filter).length;

export const isAdvancedFilterEmpty = (
	filter: ContactsAdvancedFilter
): boolean => countAdvancedFilterValues(filter) === 0;

export const removeAdvancedFilterValue = (
	filter: ContactsAdvancedFilter,
	key: AdvancedFilterFieldKey,
	value: string
): ContactsAdvancedFilter => ({
	...filter,
	[key]: filter[key].filter((current: ContactFilterValue) => current.value !== value),
});

/** Strips the display labels, leaving what the search index actually matches on. */
export const toAdvancedFilterRequest = (
	filter: ContactsAdvancedFilter
): Record<AdvancedFilterFieldKey, string[]> =>
	Object.fromEntries(
		advancedFilterFields.map(({ key }) => [
			key,
			filter[key].map((entry: ContactFilterValue) => entry.value),
		])
	) as Record<AdvancedFilterFieldKey, string[]>;
